/**
 * useGridTessellation — auto-expand rows so column widths always sum to 12.
 *
 * After every layout change:
 *  1. Group items by y-coordinate (same row).
 *  2. For each row, calculate remaining space: 12 - sum(widths).
 *  3. Distribute leftover columns evenly across row items.
 *  4. Normalize heights across row items (max height wins) to kill stair-steps.
 *
 * This creates the "edge-to-edge zero-gap" effect required by Directive 13.
 */
import { useCallback, useRef } from "react";

const COLS = 12;
const TOLERANCE = 0.01; // float rounding tolerance

/**
 * tessellateLayout — pure function that re-packs a layout array.
 * @param {Array} layout — react-grid-layout layout items [{i, x, y, w, h, ...}]
 * @param {Set} [hiddenIds] — widget IDs currently hidden (skip them)
 * @returns {Array} — a new layout with adjusted w/h values
 */
export function tessellateLayout(layout, hiddenIds = new Set()) {
    if (!layout || layout.length === 0) return layout;

    // Filter visible items only
    const visible = layout.filter((item) => !hiddenIds.has(item.i));
    if (visible.length === 0) return layout;

    // Group by y-coordinate (row buckets)
    const rowMap = new Map();
    for (const item of visible) {
        const key = item.y;
        if (!rowMap.has(key)) rowMap.set(key, []);
        rowMap.get(key).push(item);
    }

    // Tessellate each row
    const patched = new Map();
    for (const [, rowItems] of rowMap) {
        // Sort left-to-right by x
        rowItems.sort((a, b) => a.x - b.x);

        const totalW = rowItems.reduce((s, it) => s + it.w, 0);
        const gap = COLS - totalW;

        if (gap > 0 && rowItems.length > 0) {
            // Distribute extra columns across row items (larger items get more)
            const perItem = Math.floor(gap / rowItems.length);
            let remainder = gap - perItem * rowItems.length;

            let currentX = 0;
            for (const item of rowItems) {
                let extraW = perItem;
                if (remainder > 0) {
                    extraW += 1;
                    remainder -= 1;
                }
                patched.set(item.i, {
                    ...item,
                    x: currentX,
                    w: Math.min(COLS, item.w + extraW),
                });
                currentX += (item.w + extraW);
            }
        } else {
            // Row is already full — just normalize x positions
            let currentX = 0;
            for (const item of rowItems) {
                patched.set(item.i, { ...item, x: currentX });
                currentX += item.w;
            }
        }

        // Height normalization: all items in the same row share the max height
        const maxH = Math.max(...rowItems.map((it) => (patched.get(it.i) || it).h));
        for (const item of rowItems) {
            const p = patched.get(item.i) || item;
            patched.set(item.i, { ...p, h: maxH });
        }
    }

    // Merge patched items back with hidden items (unchanged)
    return layout.map((item) => patched.get(item.i) || item);
}

/**
 * useGridTessellation — React hook wrapper.
 * Returns a `tessellate` callback and a `onLayoutChange` handler.
 */
export default function useGridTessellation(hiddenWidgetIds = new Set()) {
    const hiddenRef = useRef(hiddenWidgetIds);
    hiddenRef.current = hiddenWidgetIds;

    const tessellate = useCallback(
        (layout) => tessellateLayout(layout, hiddenRef.current),
        [],
    );

    return { tessellate };
}
